import {inject, Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {TranslateService} from '@ngx-translate/core';
import {
  Brand,
  IBusinessPartner,
  IBusinessRelationship,
  IEquipmentOption,
  IVehicle,
  TransmissionType,
  VehicleType
} from '@service-and-repairs/awpintegrationlib';
import {AuthInfoFactory} from '../auth/auth-info-factory';
import {AuthenticationTokenBearerService} from '../auth/authentication-token-bearer.service';
import {OidcToken} from '../auth/oidc/oidc-token';
import {DmsService} from '../components/dms-search/dms.service';
import {AppsService} from '../core/apps/services/apps.service';
import {ConfigurationLoader} from '../core/configuration/services/configuration.loader';
import {ConfigurationService} from '../core/configuration/services/configuration.service';
import {KeyData} from '../core/keydata/interfaces/key-data';
import {KeyDataLoader} from '../core/keydata/services/key-data.loader';
import {ServiceCase} from '../core/service-case/models/service-case';
import {ServiceCaseHolder} from '../core/service-case/models/service-case-holder';
import {UserData} from '../core/user/models/user-data';
import {UserService} from '../core/user/services/user.service';
import {EnvironmentUrlKey} from '../interfaces/IEnvironment';
import {IIframeAppConfiguration} from '../interfaces/IIframeAppConfiguration';
import {IWebComponentAppConfiguration} from '../interfaces/IWebComponentAppConfiguration';
import {AirParamService} from './air-param.service';

/**
 * Service to provide attributes (inputs) for web components.
 */
@Injectable({
  providedIn: 'root'
})
export class WebComponentAttributeService {
  private readonly dmsService: DmsService = inject(DmsService);
  private readonly configurationLoader: ConfigurationLoader = inject(ConfigurationLoader);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly userService: UserService = inject(UserService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly keyDataLoader: KeyDataLoader = inject(KeyDataLoader);
  private readonly authenticationTokenBearerService: AuthenticationTokenBearerService = inject(
    AuthenticationTokenBearerService);
  private readonly appsService: AppsService = inject(AppsService);

  user: UserData;

  constructor() {
    const userService: UserService = this.userService;
    userService.user$.subscribe((user: UserData): UserData => this.user = user);
  }

  getAttributesForApp(app: IWebComponentAppConfiguration): object {
    const cavorsSessionId: string = sessionStorage.getItem('cavors-session-id');
    const attributes: object = {
      'environment': environment.deploymentEnvironment,
      'auth-info': AuthInfoFactory.makeAuthInfo(this.user, this.serviceCaseHolder.getActiveCase()?.getBrand()),
      'headers': this.getHeaders(),
      'cavors-session-id': cavorsSessionId || '',
      'analytics-id': cavorsSessionId || '' // legacy attribute name
    };
    return Object.assign(attributes, this.getAppSpecificAttributes(app));
  }

  /**
   * Any parameters for an iframe url or a web component have to be set here.
   *
   * @param app The app shown in the iframe / web component.
   */
  private getAppSpecificAttributes(app: IIframeAppConfiguration | IWebComponentAppConfiguration): object {
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const brand: string = serviceCase?.getBrand();

    switch (app.routingPath) {
      case 'search':
        return this.getPairSearchParams();
      case 'casedetails':
        return this.getServiceCaseDetailsParams();
      case 'caseoverview':
        return this.getServiceCaseOverviewParams();
      case 'partsorderlist':
        return this.getPartsOrderListParams();
      case 'ccrcarpark':
        return {
          'base-url': this.getUrlFromEnvironmentConfig('dealerCockpit')
        };
      case 'ccrreporting':
        return {
          'base-url': this.getUrlFromEnvironmentConfig('dealerCockpit'),
          language: this.user.getLocale()
        };
      case 'feedbackadmin':
        return this.getFeedbackAdminParams();
      case 'vehicledetails':
        return this.getVehicleDetailsParams();
      case 'diagnosticprotocols':
        return {
          vin: serviceCase.getVinLong()
        };
      case 'feedback':
        return this.getFeedbackParams();
      case 'partssearch':
        return this.getPartsSearchParams();
      case 'partsusage':
        return this.getPartsUsageParams();
      case 'partsinterpretation':
        return this.getPartsInterpretationParams();
      case 'vehiclehistory':
        return this.getVehicleHistoryParams();
      case 'flatratesearch':
        return this.getFlatRateUnitsParams();
      case 'multiserviceleads':
      case 'serviceleads': {
        const fleetParams: object = this.getLeadsParameters();
        Object.assign(fleetParams, {
          'outlet-members': this.user.getAllOutletPeers(),
          'specialist-groups': this.configurationLoader.getSpecialistGroups(),
          'cosy-base-url': this.getUrlFromEnvironmentConfig('cosy'),
          'los-base-url': this.getUrlFromEnvironmentConfig('los'),
          'scc-appointments-base-url': this.getUrlFromEnvironmentConfig('sccAppointmentsService'),
          'customer-data-base-url': this.getUrlFromEnvironmentConfig('customerData')
        });
        return fleetParams;
      }
      case 'servicedemand': {
        const serviceDemandParams: object = this.getLeadsParameters(brand);
        Object.assign(serviceDemandParams, {
          'cosy-base-url': this.getUrlFromEnvironmentConfig('cosy'),
          'los-base-url': this.getUrlFromEnvironmentConfig('los'),
          'check-control-message-base-url': this.getUrlFromEnvironmentConfig('checkControlMessage'),
          'service-planning-base-url': this.getUrlFromEnvironmentConfig('servicePlanning'),
          'remote-key-read-base-url': this.getUrlFromEnvironmentConfig('remoteKeyRead'),
          'measure-base-url': this.getUrlFromEnvironmentConfig('airServices'),
          'navigation-map-base-url': this.getUrlFromEnvironmentConfig('airServices'),
          'esl-document-base-url': this.getUrlFromEnvironmentConfig('airServices'),
          'sel-document-base-url': this.getUrlFromEnvironmentConfig('airServices'),
          'sib-document-base-url': this.getUrlFromEnvironmentConfig('airServices'),
          'fault-codes-base-url': this.getUrlFromEnvironmentConfig('faultCodes'),
          'esa-base-url': this.getUrlFromEnvironmentConfig('esa'),
          'scc-appointments-base-url': this.getUrlFromEnvironmentConfig('sccAppointmentsService'),
          'service-case-id': this.serviceCaseHolder.getActiveCase()?.getExternalId(),
          'edit-mileage-base-url': this.getUrlFromEnvironmentConfig('editMileage'),
          'pressure-unit': this.user.getPressureUnit().toUpperCase(),
          'dcom-workplace-id': this.dmsService.getWorkplaceId(brand),
          'acs-base-url': this.getUrlFromEnvironmentConfig('acs'),
          'tsdc-base-url': this.getUrlFromEnvironmentConfig('dealerCockpit'),
          'service-contracts-base-url': this.getUrlFromEnvironmentConfig('serviceContracts'),
          'customer-data-base-url': this.getUrlFromEnvironmentConfig('customerData')
        });
        this.applyTreadDepthUnitForLeads(serviceDemandParams, this.user.getTreadDepthUnit());
        return serviceDemandParams;
      }
      case 'marketconfiguration':
      case 'editingaudit':
      case 'potentiallist':
      case 'dealerdashboard':
        return {
          language: this.user.getLocale()
        };
      case 'servicepreparation':
        return this.getServicePreparationParameter(serviceCase);
      case 'aicompanion':
        return {
          lang: this.user.getLocale(),
          vin17: serviceCase.getVinLong()
        };
      default:
        return {};
    }
  }

  /**
   * Returns the headers a web component should add to its requests.
   */
  private getHeaders(): { [key: string]: string } {
    const headers: { [p: string]: string } = this.authenticationTokenBearerService.oidcTokenSubject
      .getValue()
      .getHeaders();
    headers['Auth-App-Id'] = 'awp';
    headers['Cavors-Session-Id'] = sessionStorage.getItem('cavors-session-id') || '';
    return headers;
  }

  private getUrlFromEnvironmentConfig(key: EnvironmentUrlKey): string {
    if (!environment.urls[key]) {
      console.warn('Route Factory Service: URL "' + key + '" not found in environment.ts.');
      return undefined;
    }
    return ConfigurationService.getUrlForPortalHost(key);
  }

  private getServicePreparationParameter(serviceCase: ServiceCase): object {
    return {
      gCID: serviceCase?.getGcdmCustomerId(),
      brand: serviceCase?.getBrand(),
      vin: serviceCase?.getVinLong()
    };
  }

  private getFeedbackParams(): object {
    return {
      'app-list': this.appsService.getVisibleApps()
        .map((a: IIframeAppConfiguration | IWebComponentAppConfiguration): { id: string, label: string } => ({
          id: a.titleResource,
          label: this.translate.instant(a.titleResource)
        }))
    };
  }

  private getFeedbackAdminParams(): object {
    return {
      'app-list': this.appsService.getAllApps()
        .map((app: any): object => ({id: app.titleResource, label: this.translate.instant(app.titleResource)})),
      'page-size': 50,
      language: this.user.getLocale()
    };
  }

  private getLeadsParameters(brand?: Brand | string): object {
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const businessPartner: IBusinessPartner = this.user?.getBusinessPartner();
    const leadsBusinessPartner: object = {
      'dpNo': businessPartner?.getDistributionPartnerNumber(),
      'businessRelationships': businessPartner?.getBusinessRelationships()
        .map((businessRelationship: IBusinessRelationship): object => ({
          'buno': businessRelationship.getBusinessNumber(),
          'brands': businessRelationship.getBrands()
        }))
    };
    const businessPartnerIds: string[] = this.user?.getBusinessPartners()
      .map((partner: IBusinessPartner) => partner.getBusinessPartnerId());
    const keyData: KeyData = this.keyDataLoader.getKeyDataForVin(this.serviceCaseHolder.getActiveCase()?.getVinLong());

    return {
      vin: serviceCase?.getVinLong(),
      'distribution-partner-number': businessPartner?.getDistributionPartnerNumber(),
      'outlet-number': businessPartner?.getOutletNumber(),
      'business-number': this.user.getBusinessNumber(brand),
      'distance-unit': this.user.getMileageUnit(),
      country: this.user.getB2XCountryCode(),
      language: this.user.getLocale(),
      brand: brand?.toString(),
      'mileage': serviceCase?.getMileageRecord(),
      'mileage-in-kilometers': serviceCase?.getCurrentMileageInKilometers(),
      'business-partner': leadsBusinessPartner,
      'business-partners': businessPartnerIds.join(','),
      'business-partner-id': this.user?.getBusinessPartner().getBusinessPartnerId(),
      'battery-state-of-charge-in-percent': keyData?.batteryStateOfChargeInPercent,
      'fuel-tank-level-in-liter': keyData?.fuelTankLevelInLiter,
      'mileage-in-kilometers-from-latest-key-read': keyData?.totalDistanceInKm,
      'data-read-date': keyData?.readInDate,
      'data-write-date': keyData?.writeDate,
      'data-read-provider': keyData?.keyOrigin,
      'user-full-name': this.user?.getName()
    };
  }

  private getServiceCaseDetailsParams(): object {
    return {
      'active-case-data': this.serviceCaseHolder.getActiveCase(),
      'user-data': this.user,
      'kai-base-url': this.getUrlFromEnvironmentConfig('mileageService'),
      'tsara-base-url': this.getUrlFromEnvironmentConfig('tsara'),
      'appointment-service-base-url': this.getUrlFromEnvironmentConfig('appointmentService'),
      'pair-base-url': this.getUrlFromEnvironmentConfig('pairBaseUrl'),
      'is-flat-rate-unit-amount-zero-allowed': this.configurationLoader.isFlatRateUnitAmountZeroAllowed(),
      'show-flat-rate-unit-amount': this.configurationLoader.showFlatRateUnitValues(),
      'is-appointment-scheduling-enabled': this.configurationLoader.isAppointmentSchedulingEnabled(),
      'dms-order-required-for-appointment-scheduling-system': this.configurationLoader.isDmsOrderRequiredForAppointmentSchedulingSystem(),
      'is-soft-planning-enabled': this.configurationLoader.isSoftPlanningEnabled(),
      'soft-planning-base-url': this.getUrlFromEnvironmentConfig('softPlanning'),
      'is-soft-planning-preview-enabled': environment.softPlanningPreviewEnabled,
      'parts-ordering': this.appsService.appVisibleForUser('partsordering'),
      'show-full-retail-saving': this.configurationLoader.showFullRetailSaving(),
      'dms-settings': this.dmsService.dmsSettings,
      'leads-dealer-config': this.configurationLoader.getLeadsDealerConfig(),
      'specialist-groups': this.configurationLoader.getSpecialistGroups(),
      'esh-write-mode': this.user.getEshWriteMode(),
      // Unset values must be passed as empty strings: an undefined attribute value becomes the string "undefined",
      // which would prevent the fallback to the ETK DMS settings in the web component.
      'parts-dms-employee-number': this.user.getPartsDmsEmployeeNumber() ?? '',
      'parts-dms-user-password': this.user.getPartsDmsUserPassword() ?? ''
    };
  }

  private getServiceCaseOverviewParams(): object {
    return {
      'user-data': this.user,
      'specialist-groups': this.configurationLoader.getSpecialistGroups()
    };
  }

  private getPartsOrderListParams(): object {
    return {
      'user-data': this.user,
      'etk-dms-settings': this.dmsService.dmsSettings.etk,
      'specialist-groups': this.configurationLoader.getSpecialistGroups(),
      'pair-base-url': this.getUrlFromEnvironmentConfig('pairBaseUrl')
    };
  }

  private getAirWebComponentParams(): object {
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const businessNumber: string = this.user.getBusinessNumber(serviceCase?.getBrand());
    const token: OidcToken = this.authenticationTokenBearerService.oidcTokenSubject.getValue();

    const params: object = {
      language: this.user?.getLocale(),
      'app-id': 'awp',
      vin: serviceCase?.getVinLong(),
      'auth-scheme': token.business_context.substring(0, 3),
      'auth-type': 'WEN_TOKEN',
      'access-token': token.access_token
    };
    Object.assign(params, {'dealer-code': businessNumber});
    return params;
  }

  private getFlatRateUnitsParams(): object {
    const params: object = this.getAirWebComponentParams();
    Object.assign(params, {'flatrate-service-base-url': this.getUrlFromEnvironmentConfig('airServices')});
    Object.assign(params, {'show-packages-button': false});
    Object.assign(params, {'show-repair-instructions-button': false});
    Object.assign(params, {'show-defect-codes-button': false});
    Object.assign(params, {'show-flatrate-values': this.configurationLoader.showFlatRateUnitValues()});

    return params;
  }

  private getVehicleHistoryParams(): object {
    const params: object = this.getAirWebComponentParams();
    Object.assign(params, {'base-url': this.getUrlFromEnvironmentConfig('airServices')});
    Object.assign(params, {'mileage-unit': AirParamService.convertMileageUnitForAir(this.user?.getMileageUnit())});
    if (this.isWarrantyHistoryDisabledInUserCountry()) {
      Object.assign(params, {'provide-warranty-history': false});
    }
    Object.assign(
      params,
      {'allow-delete-service-history-entry': this.configurationLoader.isServiceHistoryDeletionAllowed()}
    );

    return params;
  }

  private isWarrantyHistoryDisabledInUserCountry(): boolean {
    // General dealers in the asian markets China, Japan and Korea must not see the warranty history
    return ['KR', 'JP', 'CN'].includes(this.user.getB2XCountryCode());
  }

  private getPartsSearchParams(): object {
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();

    return {
      language: this.user.getLocale(),
      defaultPartsSearchBrand: this.user.getDefaultPartsSearchBrand(),
      addedQuantityStrategy: this.user.getAddedQuantityStrategy(),
      showPicturePanelTreeSearch: this.user.getShowPicturePanelTreeSearch(),
      showPartNumbers: this.user.getShowPartNumbers(),
      showRetrofittableParts: this.user.getShowRetrofittableParts(),
      showOmittedParts: this.user.getShowOmittedParts(),
      autoExpandExpandableSections: this.user.getAutoExpandExpandableSections(),
      vehicle: this.getVehicleParamsForPartsSearch(serviceCase?.getVehicle()),
      'base-url': this.getUrlFromEnvironmentConfig('carsServices'),
      'service-case-id': serviceCase?.getExternalId(),
      'currency-code': serviceCase?.getCurrencyCode(),
      'session-id': sessionStorage.getItem('cavors-session-id'),
      'dms-settings': this.dmsService.dmsSettings,
      'dms-customer-number': this.serviceCaseHolder.getActiveCase()?.getDmsCustomerNumber(),
      'tsara-url': this.getUrlFromEnvironmentConfig('tsara'),
      'transfer-mode': this.dmsService.dmsSettings.transferMode,
      'workplace-id': this.dmsService.getWorkplaceId(this.serviceCaseHolder.getActiveCase()?.getBrand())
    };
  }

  private getPartsUsageParams(): object {
    return {
      'base-url': this.getUrlFromEnvironmentConfig('carsServices'),
      'network-context': ConfigurationService.usesIntranetUrls() ? 'intranet' : 'internet',
      language: this.user.getLocale(),
      country: this.user.getB2XCountryCode(),
      'app-id': 'AWP'
    };
  }

  private getPartsInterpretationParams(): object {
    return {
      'base-url': this.getUrlFromEnvironmentConfig('carsServices'),
      'app-id': 'awp',
      language: this.user.getLocale()
    };
  }

  private getVehicleParamsForPartsSearch(vehicle: IVehicle): object {
    const params: object = {};

    if (vehicle?.getVin()) {
      Object.assign(params, {vin: vehicle.getVin()});
    }

    this.applyVehicleTypeForPartsSearch(params, vehicle?.getVehicleType());

    this.applyBrandForPartsSearch(params, vehicle?.getBrand());

    this.applyTransmissionTypeForPartsSearch(params, vehicle?.getTransmissionType());

    if (vehicle?.getTypeCode()) {
      Object.assign(params, {typeCode: vehicle.getTypeCode()});
    }
    if (vehicle?.getBaseTypeCode()) {
      Object.assign(params, {baseTypeCode: vehicle.getBaseTypeCode()});
    }

    if (vehicle?.getProductionDate()) {
      Object.assign(params, {productionDate: vehicle.getProductionDate()});
    }

    if (vehicle?.getUpholsteryCode()) {
      Object.assign(params, {upholsteryCode: vehicle.getUpholsteryCode()});
    }

    if (vehicle?.getPaintNumber()) {
      Object.assign(params, {paint: vehicle.getPaintNumber()});
    }

    if (vehicle?.getEquipmentOptions()) {
      const equipmentOptionCodes: string[] = vehicle.getEquipmentOptions()
        .map((option: IEquipmentOption) => option.getCode());
      Object.assign(params, {equipmentOptions: equipmentOptionCodes});
    }

    return params;
  }

  private applyTreadDepthUnitForLeads(params: object, unit: string): void {
    if (unit === 'mm') {
      Object.assign(params, {'tread-depth-unit': 'MILLIMETER'});
    } else {
      Object.assign(params, {'tread-depth-unit': unit.toUpperCase()});
    }
  }

  private applyVehicleTypeForPartsSearch(params: object, vehicleType: VehicleType): void {
    if (vehicleType === VehicleType.PASSENGER_CAR) {
      Object.assign(params, {productType: 'CAR'});
    } else if (vehicleType) {
      Object.assign(params, {productType: vehicleType});
    }
  }

  private applyBrandForPartsSearch(params: object, brand: Brand): void {
    if (brand === Brand.RR) {
      Object.assign(params, {brand: 'ROLLS_ROYCE'});
    } else if (brand === Brand.BMW_i) {
      Object.assign(params, {brand: 'BMW_I'});
    } else if (brand === Brand.MOT) {
      Object.assign(params, {brand: 'BMW'});
    } else if (brand) {
      Object.assign(params, {brand: brand});
    }
  }

  private applyTransmissionTypeForPartsSearch(params: object, transmission: TransmissionType): void {
    if (transmission === TransmissionType.AUTOMATIC_TRANSMISSION) {
      Object.assign(params, {transmissionType: 'AUTOMATIC'});
    } else if (transmission === TransmissionType.MANUAL_TRANSMISSION) {
      Object.assign(params, {transmissionType: 'MANUAL'});
    } else if (transmission === TransmissionType.UNKNOWN) {
      Object.assign(params, {transmissionType: 'NEUTRAL'});
    }
  }

  private getPairSearchParams(): object {
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const flatRateUnitConfiguration = {
      showFlatRateUnitValues: this.configurationLoader.showFlatRateUnitValues(),
      flatRateUnitAdditionalInformationMode: 'showAll',
      // TODO: Should be put to user settings.
      // Possible values: "showPackages" | "showPackagesWithEnforcedStandardScope" | "hidePackages"
      packagesForFlatRateUnitsMode: 'showPackages'
    };
    const params = {
      'base-url': this.getUrlFromEnvironmentConfig('pairBaseUrl'),
      'language': this.user.getLocale(),
      'network-context': ConfigurationService.usesIntranetUrls() ? 'intranet' : 'internet',
      'flat-rate-unit-configuration': flatRateUnitConfiguration,
      'country': this.user.getB2XCountryCode(),
      'parts-catalog-configuration': this.getPartsCatalogConfigurationForPairSearch(serviceCase),
      'retrieve-prices-and-availabilities-automatically': this.configurationLoader.getDmsConfiguration()?.isAutomaticPriceAndStockRetrievalEnabled,
      'app-id': 'AWP'
    };

    if (serviceCase?.getVinLong()) {
      Object.assign(params, {
        'vehicle-context': {
          vin: serviceCase.getVinLong(),
          typeCode: serviceCase.getTypeCode(),
          productionDate: serviceCase.getProductionDate(),
          paint: serviceCase.getPaint(),
          upholsteryCode: serviceCase.getVehicle()?.getUpholsteryCode(),
          equipmentOptions: serviceCase.getVehicle()?.getEquipmentOptions(),
          brand: AuthInfoFactory.awpBrandToCsslBrand(serviceCase.getBrand() as Brand),
          productType: this.getPairSearchProductType(serviceCase),
        }
      });
    } else if (serviceCase?.getTypeCode()) {
      Object.assign(params, {
        'vehicle-context': {
          typeCode: serviceCase.getTypeCode(),
          baseTypeCode: serviceCase.getVehicle()?.getBaseTypeCode(),
          transmissionType: getPairSearchTransmissionType(serviceCase.getVehicle()?.getTransmissionType()),
          brand: AuthInfoFactory.awpBrandToCsslBrand(serviceCase.getBrand() as Brand),
          productType: this.getPairSearchProductType(serviceCase),
        }
      });
    }

    function getPairSearchTransmissionType(awpTransmissionType: TransmissionType): string {
      // Must be one of: AUTOMATIC, MANUAL, NEUTRAL, WITHOUT, or an empty string.
      switch (awpTransmissionType) {
        case TransmissionType.AUTOMATIC_TRANSMISSION:
          return 'AUTOMATIC';
        case TransmissionType.MANUAL_TRANSMISSION:
          return 'MANUAL';
        default:
          return '';
      }
    }

    return params;
  }

  private getPairSearchProductType(serviceCase: ServiceCase): 'CAR' | 'MOTORCYCLE' {
    const vehicleType: VehicleType = serviceCase?.getVehicle()?.getVehicleType();
    return vehicleType === VehicleType.MOTORCYCLE || serviceCase?.getBrand() === Brand.MOT
      ? 'MOTORCYCLE'
      : 'CAR';
  }

  private getPartsCatalogConfigurationForPairSearch(serviceCase: ServiceCase): object {
    return {
      defaultpartssearchbrand: this.user.getDefaultPartsSearchBrand(),
      addedquantitystrategy: this.user.getAddedQuantityStrategy(),
      showpicturepaneltreesearch: this.user.getShowPicturePanelTreeSearch(),
      showpartnumbers: this.user.getShowPartNumbers(),
      showretrofittableparts: this.user.getShowRetrofittableParts(),
      showomittedparts: this.user.getShowOmittedParts(),
      autoexpandexpandablesections: this.user.getAutoExpandExpandableSections(),
      serviceCaseId: serviceCase?.getExternalId(),
      currencyCode: serviceCase?.getCurrencyCode(),
      dmsSettings: this.dmsService.dmsSettings,
      dmsCustomerNumber: serviceCase?.getDmsCustomerNumber(),
      tsaraUrl: this.getUrlFromEnvironmentConfig('tsara'),
      workplaceId: this.dmsService.getWorkplaceId(serviceCase?.getBrand())
    };
  }

  private getVehicleDetailsParams(): object {
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const params: { [key: string]: unknown } = {
      'base-url': this.getUrlFromEnvironmentConfig('pairBaseUrl'),
      'network-context': ConfigurationService.usesIntranetUrls() ? 'intranet' : 'internet',
      'language': this.user.getLocale(),
      'country': this.user.getB2XCountryCode(),
      'app-id': 'AWP',
      'mileage': serviceCase.getMileageRecord()?.value,
      'distance-unit': this.user.getMileageUnit(),
      'service-case-id': serviceCase.getExternalId()
    };
    if (serviceCase.getVinLong()) {
      params['vehicle-context'] = {
        vin: serviceCase.getVinLong(),
        brand: AuthInfoFactory.awpBrandToCsslBrand(serviceCase.getBrand() as Brand)
      };
    } else {
      params['vehicle-context'] = {
        typeCode: serviceCase.getTypeCode(),
        brand: AuthInfoFactory.awpBrandToCsslBrand(serviceCase.getBrand() as Brand)
      };
    }
    return params;
  }
}
